import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { warehouseAPI, itemsAPI, vansAPI, categoriesAPI, vendorsAPI } from '../lib/api';
import { perf } from '../lib/perf';
import { checkPlanLimit, handleApiError, showPlanLimitToast, getShortLimitMessage } from '../lib/apiErrors';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { SearchableSelect } from '../components/ui/searchable-select';
import { AsyncSearchableSelect } from '../components/ui/async-searchable-select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../contexts/AuthContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import { 
  Warehouse as WarehouseIcon, Package, Plus, Loader2, Edit2, Save, X, 
  AlertTriangle, ArrowRightLeft, Download, Search, ExternalLink, MapPin, Layers, Settings, ScanLine, Settings2, Upload, MoreVertical, Filter, ClipboardCheck, History, ChevronDown, Handshake, Briefcase, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { ScanManager } from '../components/ScanManager';
import { AvailabilityBreakdownModal, useAvailabilityBreakdown } from '../components/AvailabilityBreakdownModal';
import { QtyCells, OverCommittedBanner } from '../components/WarehouseQtyCells';
import { LowStockAlerts, RestockSuggestions } from '../components/LowStockAlerts';
import { ManualAdjustmentModal } from '../components/modals/ManualAdjustmentModal';
import { TransferModal } from '../components/modals/TransferModal';
import { StartCycleCountModal } from '../components/modals/StartCycleCountModal';
import { WarehouseDetailsModal } from '../components/modals/WarehouseDetailsModal';
import { CycleCountHistory } from '../components/cycle-count/CycleCountHistory';
import { LastCycleCountIndicator } from '../components/cycle-count/LastCycleCountIndicator';
import { ListPagination } from '../components/ListPagination';
import { AssignToJobModal } from '../components/AssignToJobModal';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { PendingTransfersSection } from '../components/PendingTransfersSection';
import { KebabMenu } from '../components/KebabMenu';
import { useInventoryView } from '../hooks/useInventoryView';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { InventoryColumnSelector } from '../components/InventoryColumnSelector';
import { MobileSortSelect } from '../components/VehicleInventoryToolbar';import { SortableTableHead } from '../components/ui/SortableTableHead';

const WAREHOUSE_SORT_COLUMNS = [
  { key: 'item_name', type: 'text' },
  { key: 'sku', type: 'text' },
  { key: 'location_label', type: 'text' },
  { key: 'qty_on_hand', type: 'number' },
  { key: 'available_qty', type: 'number' },
  { key: 'allocated_qty', type: 'number' },
  { key: 'reserved_qty', type: 'number' },
  { key: 'quarantined_qty', type: 'number' },
  { key: 'effective_reorder_threshold', type: 'number' },
];

export const Warehouse = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isTech, permissions, hasPerm } = useAuth();
  
  // Check if user can manage billing (for showing Upgrade CTA / Go to Billing link)
  // permissions is an object with permission keys as properties
  const canManageBilling = isOwner || permissions?.['billing.manage'] === true;
  
  // Check if user can create/manage warehouses (for showing Add Warehouse button)
  // Uses warehouse.manage permission - NOT just isOwner or warehouse.adjust
  const canManageWarehouses = isOwner || permissions?.['warehouse.manage'] === true;
  
  // Use WarehouseContext for warehouse state
  const { 
    warehouses, 
    selectedWarehouseId, 
    selectedWarehouse,
    setSelectedWarehouseId, 
    refreshWarehouses,
    isMultiWarehouse,
    loading: warehouseContextLoading 
  } = useWarehouse();
  
  const [inventory, setInventory] = useState([]);
  const whView = useInventoryView('warehouse');
  const { sortKey: whSortKey, sortDir: whSortDir, isVisible: col } = whView;
  const whOnSort = (k) => { whView.setSort(k); setPage(1); };
  const sortedInventory = inventory;
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [warehouseDetailsOpen, setWarehouseDetailsOpen] = useState(false);
  const [breakdownState, openBreakdown, setBreakdownOpen] = useAvailabilityBreakdown();
  
  // Phase 3.5: URL-synced filter and pagination state
  const searchQuery = searchParams.get('search') || '';
  const categoryFilter = searchParams.get('category') || '';
  const lowStockOnly = searchParams.get('low_stock') === 'true';
  const ownershipFilter = searchParams.get('ownership') || '';
  const page = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('per_page')) || 25;
  
  // Search is fully decoupled from React Router to avoid per-keystroke navigation.
  // searchInput = controlled input value (instant)
  // debouncedSearch = value used by fetchData (delayed 300ms)
  // URL is synced silently via history.replaceState (no RR navigation)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const searchDebounceRef = useRef(null);
  
  // Debounce: searchInput → debouncedSearch + silent URL sync
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchInput === debouncedSearch) return;
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      // Silently update URL bar without triggering React Router navigation
      const url = new URL(window.location.href);
      if (searchInput) {
        url.searchParams.set('search', searchInput);
      } else {
        url.searchParams.delete('search');
      }
      url.searchParams.delete('page');
      window.history.replaceState(null, '', url.pathname + (url.search || ''));
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput, debouncedSearch]);
  
  // Handle browser back/forward: sync URL → local search state
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const urlSearch = params.get('search') || '';
      setSearchInput(urlSearch);
      setDebouncedSearch(urlSearch);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Server response pagination data
  const [total, setTotal] = useState(0);
  const [overCommittedOnly, setOverCommittedOnly] = useState(false);
  const [overCommittedCount, setOverCommittedCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Helper to update URL params
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || value === 'false' || value === 'all' || (key === 'page' && value === 1) || (key === 'per_page' && value === 25)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // URL-synced setters (search is handled by debounce above)
  const setCategoryFilter = useCallback((value) => {
    updateParams({ category: value === 'all' ? null : value, page: 1 });
  }, [updateParams]);
  
  const setLowStockOnly = useCallback((value) => {
    updateParams({ low_stock: value ? 'true' : null, page: 1 });
  }, [updateParams]);
  
  const setOwnershipFilter = useCallback((value) => {
    updateParams({ ownership: value, page: 1 });
  }, [updateParams]);
  
  const setPage = useCallback((value) => {
    updateParams({ page: value });
  }, [updateParams]);
  
  const setPageSize = useCallback((value) => {
    updateParams({ per_page: value, page: 1 });
  }, [updateParams]);
  
  // Mobile UI state
  const [fabOpen, setFabOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  
  // Modal state
  const [actionModal, setActionModal] = useState({ open: false, type: null });
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [locationLabel, setLocationLabel] = useState('');
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Bulk restock state
  const [bulkItems, setBulkItems] = useState([{ item_id: '', name: '', quantity: '', location_label: '', note: '', ownership_type: 'owned', vendor_id: '' }]);
  
  // Consigned inventory state
  const [vendors, setVendors] = useState([]);
  const [consignedInventoryEnabled, setConsignedInventoryEnabled] = useState(false);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  
  // Location label edit state
  const [editingLocationLabel, setEditingLocationLabel] = useState(null);
  const [newLocationLabel, setNewLocationLabel] = useState('');
  
  // Threshold edit state
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [newThreshold, setNewThreshold] = useState('');
  const [useDefaultThreshold, setUseDefaultThreshold] = useState(false);
  
  // Bulk threshold state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkThresholdModal, setBulkThresholdModal] = useState(false);
  const [bulkThresholdValue, setBulkThresholdValue] = useState('');
  const [bulkUseDefault, setBulkUseDefault] = useState(false);
  
  // Threshold Import state
  const [thresholdImportModal, setThresholdImportModal] = useState(false);
  const [thresholdImportStep, setThresholdImportStep] = useState('upload'); // 'upload' | 'preview' | 'results'
  const [thresholdImportFile, setThresholdImportFile] = useState(null);
  const [thresholdImportPreview, setThresholdImportPreview] = useState(null);
  const [thresholdImportResults, setThresholdImportResults] = useState(null);
  const [thresholdImportLoading, setThresholdImportLoading] = useState(false);
  
  // Manual Adjustment modal state
  const [adjustmentModal, setAdjustmentModal] = useState({ 
    open: false, 
    itemId: null, 
    itemName: '', 
    currentQty: 0,
    defaultType: null // 'increase', 'decrease', or 'set_exact'
  });
  
  // Transfer modal state (universal TransferModal)
  const [transferModal, setTransferModal] = useState({
    open: false,
    item: null // { id, name }
  });
  
  // Cycle count modal state
  const [cycleCountModalOpen, setCycleCountModalOpen] = useState(false);
  
  // Assign to Job modal state
  const [assignJobModal, setAssignJobModal] = useState({
    open: false,
    item: null
  });
  
  // Check if user can manage cycle counts
  const canManageCycleCounts = isOwner || permissions?.['audit.manage'] === true;

  // Plan limits state
  const [planLimits, setPlanLimits] = useState(null);
  const [addWarehouseModal, setAddWarehouseModal] = useState(false);
  const [newWarehouseName, setNewWarehouseName] = useState('');
  const [addWarehouseLoading, setAddWarehouseLoading] = useState(false);

  // Count active filters (excluding search)
  const activeFilterCount = [
    categoryFilter && categoryFilter !== 'all',
    lowStockOnly,
    ownershipFilter
  ].filter(Boolean).length;

  // Clear filters except search
  const clearFiltersExceptSearch = useCallback(() => {
    updateParams({ category: null, low_stock: null, ownership: null, page: 1 });
  }, [updateParams]);

  const fetchData = useCallback(async (options = {}) => {
    const { silentRetry = false, retryCount = 0, background = false } = options;
    
    // Wait for warehouse context to be ready
    if (warehouseContextLoading || !selectedWarehouseId) {
      console.log('[WAREHOUSE_FETCH] Skipping - context not ready:', { warehouseContextLoading, selectedWarehouseId });
      return;
    }
    
    // Only set loading if not a silent retry AND this is the initial load
    if (!silentRetry && !initialLoadDone.current) {
      setLoading(true);
    }
    
    const fetchStartTime = Date.now();
    perf.markStart('warehouse-page');
    console.log('[WAREHOUSE_FETCH] Starting data fetch...', { 
      silentRetry, 
      retryCount, 
      warehouseId: selectedWarehouseId,
      filters: { categoryFilter, debouncedSearch, lowStockOnly, ownershipFilter, page, pageSize }
    });
    
    try {
      // Note: warehouse_id is automatically injected by api.js interceptor
      // Phase 3.5: Use server-side pagination for warehouse inventory
      // Items are now loaded via async search, not preloaded (supports 18k+ items)
      
      // Make calls individually to identify which one fails
      const apiCalls = [
        { name: 'inventory', call: () => warehouseAPI.getInventory({ 
          category_id: categoryFilter === 'all' ? undefined : categoryFilter || undefined,
          search: debouncedSearch || undefined,
          low_stock_only: lowStockOnly,
          over_committed_only: overCommittedOnly || undefined,
          sort_by: whSortKey,
          sort_dir: whSortDir,
          ownership_type: ownershipFilter || undefined,
          page: page,
          page_size: pageSize
        })},
        { name: 'vehicles', call: () => vansAPI.list() },
        { name: 'categories', call: () => categoriesAPI.list() },
        { name: 'planLimits', call: () => warehouseAPI.getPlanLimits() },
        { name: 'settings', call: () => warehouseAPI.getSettings() },
        { name: 'vendors', call: () => vendorsAPI.list() }
      ];
      
      const results = await Promise.all(
        apiCalls.map(async ({ name, call }) => {
          const start = Date.now();
          try {
            const result = await call();
            console.log(`[WAREHOUSE_FETCH] ${name} succeeded in ${Date.now() - start}ms`);
            return { name, success: true, data: result };
          } catch (err) {
            console.error(`[WAREHOUSE_FETCH] ${name} FAILED in ${Date.now() - start}ms:`, {
              status: err.response?.status,
              statusText: err.response?.statusText,
              data: err.response?.data,
              code: err.code,
              message: err.message
            });
            return { name, success: false, error: err };
          }
        })
      );
      
      // Check for any failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        console.error(`[WAREHOUSE_FETCH] ${failures.length} API calls failed:`, failures.map(f => f.name));
        // Throw the first error to trigger retry/error handling
        throw failures[0].error;
      }
      
      // Extract successful results
      const inventoryRes = results.find(r => r.name === 'inventory').data;
      const vehiclesRes = results.find(r => r.name === 'vehicles').data;
      const categoriesRes = results.find(r => r.name === 'categories').data;
      const limitsRes = results.find(r => r.name === 'planLimits').data;
      const settingsRes = results.find(r => r.name === 'settings').data;
      const vendorsRes = results.find(r => r.name === 'vendors').data;
      
      // Handle paginated response format: { items: [], total: N, ... }
      const inventoryData = inventoryRes.data;
      setInventory(inventoryData.items || inventoryData);
      setTotal(inventoryData.total || 0);
      setOverCommittedCount(inventoryData.over_committed_count || 0);
      setTotalPages(inventoryData.total_pages || 0);
      setVehicles(vehiclesRes.data.filter(v => v.is_active));
      setCategories(categoriesRes.data);
      setPlanLimits(limitsRes.data);
      setConsignedInventoryEnabled(settingsRes.data.consigned_inventory_enabled || false);
      setVendors(vendorsRes.data || []);
      
      console.log(`[WAREHOUSE_FETCH] All data loaded successfully in ${Date.now() - fetchStartTime}ms`);
      
      // Update edit name when selected warehouse changes
      if (selectedWarehouse && !background) {
        setEditName(selectedWarehouse.name);
      }
    } catch (error) {
      if (background) {
        console.warn('[WAREHOUSE_FETCH] background refresh failed; keeping current data', error?.message);
        return;
      }
      // If this is a silent retry (post-operation refresh), try again once
      if (silentRetry && retryCount < 2) {
        console.warn(`Silent retry ${retryCount + 1}/2 for inventory refresh...`);
        setTimeout(() => {
          fetchData({ silentRetry: true, retryCount: retryCount + 1 });
        }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s
        return;
      }
      
      // Provide specific error messages based on error type
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 403) {
        toast.error(detail || 'Permission denied: You do not have access to this warehouse');
      } else if (status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (status === 404) {
        toast.error('Warehouse or inventory data not found');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Please try again.');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network.');
      } else {
        toast.error(detail || 'Failed to load warehouse data. Please refresh the page.');
      }
      console.error('Warehouse data load error:', error);
    } finally {
      initialLoadDone.current = true;
      setLoading(false);
      perf.markEnd('warehouse-page');
    }
  }, [categoryFilter, debouncedSearch, lowStockOnly, overCommittedOnly, whSortKey, whSortDir, ownershipFilter, selectedWarehouseId, warehouseContextLoading, selectedWarehouse, page, pageSize]);
  
  // Async item search handler for Add Stock modal (supports 18k+ items)
  const handleItemSearch = useCallback(async (query, signal) => {
    try {
      const response = await itemsAPI.search(query, 25, signal);
      return (response.data || []).map(item => ({
        value: item.id,
        label: `${item.name}${item.sku ? ` (${item.sku})` : ''}`,
        sublabel: item.category || undefined
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Item search error:', error);
      }
      return [];
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Phase 6: silent revalidation (after any write, on tab focus, every 60s); open dialogs keep their state
  useLiveRefresh(() => fetchData({ silentRetry: true, background: true }));
  
  // Phase 3.5: Handle pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedItems(new Set()); // Clear selection on page change
  };
  
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setSelectedItems(new Set()); // Clear selection
  };

  // Handle warehouse selection change
  const handleWarehouseChange = (warehouseId) => {
    setSelectedWarehouseId(warehouseId);
    updateParams({ page: 1 }); // Reset to page 1 on warehouse change
    // Data will be refreshed automatically via useEffect when selectedWarehouseId changes
  };

  // Navigate to billing page for upgrade
  const handleUpgradeClick = () => {
    navigate('/billing');
  };

  // Pre-check and open Add Warehouse modal
  const handleAddWarehouseClick = () => {
    // Pre-check plan limit before opening modal
    const { canCreate, message } = checkPlanLimit(planLimits, 'warehouse');
    
    if (!canCreate) {
      showPlanLimitToast(message, 'warehouse', {
        canUpgrade: canManageBilling,
        onUpgrade: handleUpgradeClick
      });
      return;
    }
    
    setAddWarehouseModal(true);
  };

  // Get tooltip message for disabled button
  const warehouseLimitTooltip = getShortLimitMessage(planLimits, 'warehouse');
  const isAtWarehouseLimit = planLimits?.warehouse && !planLimits.warehouse.can_create && planLimits.warehouse.limit !== null;

  // Handle creating a new warehouse
  const handleCreateWarehouse = async () => {
    if (!newWarehouseName.trim()) return;
    
    // Double-check limit (race condition protection)
    const { canCreate, message } = checkPlanLimit(planLimits, 'warehouse');
    if (!canCreate) {
      showPlanLimitToast(message, 'warehouse', {
        canUpgrade: canManageBilling,
        onUpgrade: handleUpgradeClick
      });
      setAddWarehouseModal(false);
      return;
    }
    
    setAddWarehouseLoading(true);
    try {
      await warehouseAPI.createWarehouse({ name: newWarehouseName.trim() });
      // Refresh warehouses via context
      await refreshWarehouses();
      setNewWarehouseName('');
      setAddWarehouseModal(false);
      toast.success(`Warehouse "${newWarehouseName.trim()}" created`);
      // Refresh plan limits
      const limitsRes = await warehouseAPI.getPlanLimits();
      setPlanLimits(limitsRes.data);
    } catch (error) {
      // Use centralized error handler
      handleApiError(error, { defaultMessage: 'Failed to create warehouse' });
    } finally {
      setAddWarehouseLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      // Note: warehouse_id is automatically injected by api.js
      await warehouseAPI.update(editName.trim());
      // Refresh warehouses in context to update name
      await refreshWarehouses();
      setEditing(false);
      toast.success('Warehouse name updated');
    } catch (error) {
      toast.error('Failed to update warehouse name');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async () => {
    if (!selectedItemId || !quantity || parseInt(quantity) <= 0) {
      toast.error('Please select an item and enter a valid quantity');
      return;
    }

    setActionLoading(true);
    try {
      if (actionModal.type === 'restock') {
        // warehouse_id is auto-injected by api.js
        await warehouseAPI.restock({
          item_id: selectedItemId,
          quantity: parseInt(quantity),
          note: note || null,
        });
        toast.success('Item restocked');
      }
      // Note: 'use' type removed - now handled by ManualAdjustmentModal with 'decrease' preset
      // Note: 'transfer' type removed - now handled by TransferModal

      closeModal();
      fetchData();
    } catch (error) {
      if (error.isQueued) {
        toast.info("You're offline. Action queued for sync when back online.");
        closeModal();
      } else {
        toast.error(error.response?.data?.detail || 'Action failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkStock = async () => {
    const validItems = bulkItems.filter(item => (item.item_id || item.name) && parseInt(item.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one item with quantity');
      return;
    }
    
    // Validate consigned items have vendors
    const invalidConsigned = validItems.filter(
      item => item.ownership_type === 'consigned' && !item.vendor_id
    );
    if (invalidConsigned.length > 0) {
      toast.error('Consigned items require a vendor');
      return;
    }

    setActionLoading(true);
    try {
      await warehouseAPI.bulkRestock({
        items: validItems.map(item => ({
          ...(item.item_id ? { item_id: item.item_id } : { name: item.name }),
          quantity: parseInt(item.quantity),
          location_label: item.location_label || null,
          note: item.note || null,
          ownership_type: item.ownership_type || 'owned',
          vendor_id: item.ownership_type === 'consigned' ? item.vendor_id : null,
        }))
      });
      toast.success(`${validItems.length} items stocked`);
      closeModal();
      fetchData();
    } catch (error) {
      if (error.isQueued) {
        toast.info("You're offline. Action queued for sync when back online.");
        closeModal();
      } else {
        toast.error(error.response?.data?.detail || 'Stock operation failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateLocationLabel = async (itemId) => {
    try {
      await warehouseAPI.updateItem(itemId, { location_label: newLocationLabel || null });
      setInventory(prev => prev.map(inv => 
        inv.item_id === itemId ? { ...inv, location_label: newLocationLabel || null } : inv
      ));
      setEditingLocationLabel(null);
      toast.success('Location label updated');
    } catch (error) {
      toast.error('Failed to update location label');
    }
  };

  const handleUpdateThreshold = async (itemId) => {
    try {
      const payload = useDefaultThreshold 
        ? { clear_threshold_override: true }
        : { warehouse_reorder_threshold_override: newThreshold ? parseInt(newThreshold) : null };
      
      const response = await warehouseAPI.updateItem(itemId, payload);
      
      setInventory(prev => prev.map(inv => 
        inv.item_id === itemId ? { 
          ...inv, 
          warehouse_reorder_threshold_override: response.warehouse_reorder_threshold_override,
          effective_reorder_threshold: response.effective_reorder_threshold
        } : inv
      ));
      setEditingThreshold(null);
      setNewThreshold('');
      setUseDefaultThreshold(false);
      toast.success('Threshold updated');
    } catch (error) {
      toast.error('Failed to update threshold');
    }
  };

  const handleBulkThresholdUpdate = async () => {
    if (selectedItems.size === 0) {
      toast.error('Select at least one item');
      return;
    }
    
    try {
      const payload = {
        item_ids: Array.from(selectedItems),
        warehouse_reorder_threshold_override: bulkUseDefault ? null : (bulkThresholdValue ? parseInt(bulkThresholdValue) : null)
      };
      
      await warehouseAPI.bulkUpdateThreshold(payload);
      
      // Refresh data with silent retry (don't show errors on transient failures)
      await fetchData({ silentRetry: true });
      setSelectedItems(new Set());
      setBulkThresholdModal(false);
      setBulkThresholdValue('');
      setBulkUseDefault(false);
      toast.success(`Updated threshold for ${payload.item_ids.length} items`);
    } catch (error) {
      toast.error('Failed to update thresholds');
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === inventory.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(inventory.map(inv => inv.item_id)));
    }
  };

  const handleExport = async () => {
    try {
      const response = await warehouseAPI.exportCsv({ warehouse_id: selectedWarehouseId, sort_by: whSortKey, sort_dir: whSortDir });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse_inventory_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Inventory exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Threshold Import handlers
  const openThresholdImport = () => {
    setThresholdImportModal(true);
    setThresholdImportStep('upload');
    setThresholdImportFile(null);
    setThresholdImportPreview(null);
    setThresholdImportResults(null);
  };

  const closeThresholdImport = () => {
    setThresholdImportModal(false);
    setThresholdImportStep('upload');
    setThresholdImportFile(null);
    setThresholdImportPreview(null);
    setThresholdImportResults(null);
  };

  const handleThresholdFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setThresholdImportFile(file);
    setThresholdImportLoading(true);
    
    try {
      const response = await warehouseAPI.previewThresholdImport(file);
      setThresholdImportPreview(response.data);
      setThresholdImportStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse CSV');
      setThresholdImportFile(null);
    } finally {
      setThresholdImportLoading(false);
    }
  };

  const handleApplyThresholdImport = async () => {
    if (!thresholdImportFile) return;
    
    setThresholdImportLoading(true);
    try {
      const response = await warehouseAPI.applyThresholdImport(thresholdImportFile);
      setThresholdImportResults(response.data);
      setThresholdImportStep('results');
      fetchData(); // Refresh warehouse data
      toast.success(`Updated ${response.data.summary.updated_count} items, created ${response.data.summary.created_count} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setThresholdImportLoading(false);
    }
  };

  const downloadThresholdTemplate = () => {
    const template = 'sku,threshold\nEXAMPLE-SKU-001,5\nEXAMPLE-SKU-002,10';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'threshold_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const openActionModal = (type) => {
    setActionModal({ open: true, type });
    setSelectedItemId('');
    setSelectedVehicleId('');
    setQuantity('1');
    setLocationLabel('');
    setNote('');
    setBulkItems([{ item_id: '', name: '', quantity: '', location_label: '', note: '' }]);
  };

  const closeModal = () => {
    setActionModal({ open: false, type: null });
    setSelectedItemId('');
    setSelectedVehicleId('');
    setQuantity('1');
    setLocationLabel('');
    setNote('');
    setBulkItems([{ item_id: '', name: '', quantity: '', location_label: '', note: '', ownership_type: 'owned', vendor_id: '' }]);
  };

  const addBulkItem = () => {
    setBulkItems([...bulkItems, { item_id: '', name: '', quantity: '', location_label: '', note: '', ownership_type: 'owned', vendor_id: '' }]);
  };

  const updateBulkItem = (index, field, value) => {
    const updated = [...bulkItems];
    updated[index][field] = value;
    // Clear vendor_id when switching to owned
    if (field === 'ownership_type' && value === 'owned') {
      updated[index].vendor_id = '';
    }
    setBulkItems(updated);
  };

  const removeBulkItem = (index) => {
    if (bulkItems.length > 1) {
      setBulkItems(bulkItems.filter((_, i) => i !== index));
    }
  };

  // Low stock items from inventory (for alert badge)
  const lowStockItems = inventory.filter(
    inv => inv.reorder_threshold && inv.qty_on_hand <= inv.reorder_threshold
  );

  if (loading || warehouseContextLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Show message if no warehouses exist (shouldn't happen normally)
  if (!selectedWarehouse && !warehouseContextLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <WarehouseIcon className="w-12 h-12 mb-4 opacity-50" />
          <p>No warehouse available</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-4">
            {/* Warehouse icon with pencil overlay for edit details */}
            <button 
              className={`relative w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group ${canManageWarehouses ? 'cursor-pointer hover:bg-primary/20 transition-colors' : 'cursor-default'}`}
              onClick={() => {
                if (canManageWarehouses) {
                  setWarehouseDetailsOpen(true);
                }
              }}
              disabled={!canManageWarehouses}
              data-testid="warehouse-edit-trigger"
            >
              <WarehouseIcon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              {/* Pencil overlay for edit (both mobile and desktop) */}
              {canManageWarehouses && (
                <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-background border rounded-full flex items-center justify-center shadow-sm group-hover:bg-primary/10 transition-colors">
                  <Edit2 className="w-2.5 h-2.5 md:w-3 md:h-3 text-muted-foreground" />
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              {/* Warehouse Name - acts as dropdown trigger when multiple warehouses */}
              {isMultiWarehouse ? (
                /* Name is clickable dropdown when multiple warehouses exist - styled as text, not input */
                <Popover>
                  <PopoverTrigger asChild>
                    <button 
                      className="flex items-center gap-1.5 md:gap-2 group cursor-pointer hover:opacity-80 transition-opacity text-left max-w-full"
                      data-testid="warehouse-selector-trigger"
                    >
                      <h1 className="text-xl md:text-2xl font-bold truncate">{selectedWarehouse?.name}</h1>
                      <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="start">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground px-2 py-1">Switch warehouse</p>
                      {warehouses.map(wh => (
                        <button
                          key={wh.id}
                          className={`w-full text-left px-2 py-2 rounded-md text-sm transition-colors ${
                            wh.id === selectedWarehouseId 
                              ? 'bg-primary/10 text-primary font-medium' 
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            handleWarehouseChange(wh.id);
                          }}
                          data-testid={`warehouse-option-${wh.id}`}
                        >
                          {wh.name}
                          {wh.is_default && (
                            <span className="ml-2 text-xs text-muted-foreground">(Default)</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                /* Single warehouse - static name */
                <h1 className="text-xl md:text-2xl font-bold truncate">{selectedWarehouse?.name}</h1>
              )}
              <p className="text-sm text-muted-foreground">
                {total} {total === 1 ? 'item' : 'items'} in stock
                {lowStockItems.length > 0 && (
                  <span className="text-destructive ml-2">
                    • {lowStockItems.length} low stock
                  </span>
                )}
              </p>
              {/* Last Audited Indicator */}
              {canManageCycleCounts && selectedWarehouseId && (
                <LastCycleCountIndicator 
                  targetType="warehouse" 
                  targetId={selectedWarehouseId}
                />
              )}
            </div>
          </div>

          {/* Desktop Header Actions - Reorganized with primary actions visible, secondary in dropdown */}
          {canManageWarehouses && (
            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              {/* Plan limit indicator - always visible as status chip */}
              {isAtWarehouseLimit && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Badge variant="outline" className="text-muted-foreground h-9 px-3 flex items-center gap-1.5 cursor-default">
                          <WarehouseIcon className="w-3.5 h-3.5" />
                          {Math.min(planLimits.warehouse.current_count, planLimits.warehouse.limit)}/{planLimits.warehouse.limit} used
                        </Badge>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      className="flex flex-col gap-1"
                      onPointerDownOutside={(e) => e.preventDefault()}
                    >
                      <p>{warehouseLimitTooltip}</p>
                      {canManageBilling && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/billing');
                          }}
                          className="text-xs text-sky-300 underline hover:text-sky-200 cursor-pointer text-left"
                        >
                          Go to Billing
                        </button>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Secondary actions dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="warehouse-more-actions">
                    <MoreVertical className="w-4 h-4 mr-2" />
                    More
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {planLimits?.warehouse?.can_create && (
                    <DropdownMenuItem onClick={() => setTimeout(handleAddWarehouseClick, 0)} className="cursor-pointer">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Warehouse
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setTimeout(() => setScannerOpen(true), 0)} className="cursor-pointer">
                    <ScanLine className="w-4 h-4 mr-2" />
                    Scan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/warehouse/import')} className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTimeout(openThresholdImport, 0)} className="cursor-pointer">
                    <Settings2 className="w-4 h-4 mr-2" />
                    Import Thresholds
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Primary: Start Cycle Count (secondary styling) */}
              {canManageCycleCounts && (
                <Button variant="outline" onClick={() => setCycleCountModalOpen(true)} data-testid="start-cycle-count-btn">
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Start Cycle Count
                </Button>
              )}
              
              {/* Primary: Stock (primary blue button) */}
              <Button onClick={() => openActionModal('stock')} data-testid="stock-btn">
                <Plus className="w-4 h-4 mr-2" />
                Stock
              </Button>
            </div>
          )}
          {isTech && !isOwner && (
            <Button variant="outline" className="hidden md:inline-flex" onClick={() => setScannerOpen(true)}>
              <ScanLine className="w-4 h-4 mr-2" />
              Scan
            </Button>
          )}

          {/* Mobile Header Actions - Kebab for Export, icon filter, search */}
          <div className="md:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="warehouse-search-mobile"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 relative"
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                aria-label="Filters"
              >
                <Filter className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              {isOwner && (
                <KebabMenu
                  items={[
                    { label: 'Export', icon: Download, onClick: handleExport, 'data-testid': 'warehouse-export-kebab' },
                  ]}
                  data-testid="warehouse-kebab-menu"
                />
              )}
            </div>
          </div>
        </div>

        {/* Desktop Filters - Search + Filters dropdown */}
        <Card className="hidden md:block">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {/* Search input - always visible */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10"
                    data-testid="warehouse-search-input"
                  />
                </div>
              </div>
              
              {/* Filters dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    data-testid="warehouse-filters-dropdown"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                        {activeFilterCount}
                      </Badge>
                    )}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Category</Label>
                      <SearchableSelect
                        options={[
                          { value: 'all', label: 'All Categories' },
                          { value: 'uncategorized', label: 'Uncategorized' },
                          ...categories.map(cat => ({ value: cat.id, label: cat.name }))
                        ]}
                        value={categoryFilter}
                        onValueChange={setCategoryFilter}
                        placeholder="All Categories"
                        emptyText="No categories found."
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Low Stock Only</Label>
                      <Button
                        variant={lowStockOnly ? "default" : "outline"}
                        size="sm"
                        onClick={() => setLowStockOnly(!lowStockOnly)}
                      >
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {lowStockOnly ? 'On' : 'Off'}
                      </Button>
                    </div>
                    
                    {/* Ownership Filter - Only shown when consigned inventory is enabled */}
                    {consignedInventoryEnabled && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Ownership</Label>
                        <SearchableSelect 
                          options={[
                            { value: 'all', label: 'All' },
                            { value: 'owned', label: 'Owned Only' },
                            { value: 'consigned', label: 'Consigned Only' }
                          ]}
                          value={ownershipFilter || 'all'} 
                          onValueChange={(val) => setOwnershipFilter(val === 'all' ? '' : val)}
                          placeholder="All Ownership Types"
                          sortAlphabetically={false}
                        />
                      </div>
                    )}
                    
                    {activeFilterCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full" 
                        onClick={clearFiltersExceptSearch}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Low Stock chip indicator (shown when filter is active) */}
              {lowStockOnly && (
                <Badge 
                  variant="secondary" 
                  className="h-8 px-3 gap-1.5 cursor-pointer hover:bg-secondary/80"
                  onClick={() => setLowStockOnly(false)}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Low Stock
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              
              {/* Ownership Filter chip indicator */}
              {ownershipFilter && (
                <Badge 
                  variant={ownershipFilter === 'consigned' ? 'default' : 'secondary'}
                  className={`h-8 px-3 gap-1.5 cursor-pointer hover:opacity-80 ${ownershipFilter === 'consigned' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                  onClick={() => setOwnershipFilter('')}
                >
                  {ownershipFilter === 'consigned' ? (
                    <Handshake className="w-3.5 h-3.5" />
                  ) : (
                    <Package className="w-3.5 h-3.5" />
                  )}
                  {ownershipFilter === 'consigned' ? 'Consigned' : 'Owned'}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mobile Filters Dropdown (triggered by icon in header) */}
        {mobileFiltersOpen && (
          <div className="md:hidden space-y-3">
            <>
              <div 
                className="fixed inset-0 bg-black/20 z-40"
                onClick={() => setMobileFiltersOpen(false)}
              />
              <div className="relative z-50 bg-background border rounded-lg shadow-lg p-4 space-y-4">
                {/* Category Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Category</Label>
                  <SearchableSelect
                    options={[
                      { value: 'all', label: 'All Categories' },
                      { value: 'uncategorized', label: 'Uncategorized' },
                      ...categories.map(cat => ({ value: cat.id, label: cat.name }))
                    ]}
                    value={categoryFilter}
                    onValueChange={setCategoryFilter}
                    placeholder="All Categories"
                    emptyText="No categories found."
                    className="h-10"
                  />
                </div>

                {/* Low Stock Toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Low Stock Only</Label>
                  <Button
                    variant={lowStockOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLowStockOnly(!lowStockOnly)}
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {lowStockOnly ? 'On' : 'Off'}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    className="flex-1 h-10"
                    onClick={clearFiltersExceptSearch}
                  >
                    Clear
                  </Button>
                  <Button
                    className="flex-1 h-10"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    Apply
                  </Button>
                </div>

                {/* Results count */}
                <div className="text-xs text-muted-foreground text-center">
                  Showing {inventory.length} items
                </div>
              </div>
            </>
          </div>
        )}

        {/* Low Stock Alerts — Stage 6 */}
        <LowStockAlerts sourceType="warehouse" compact />
        <RestockSuggestions sourceType="warehouse" />

        {/* Inventory List */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 space-y-0">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Warehouse Inventory
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Items from your Parts List assigned to this warehouse</p>
              <OverCommittedBanner
                count={overCommittedCount}
                active={overCommittedOnly}
                onToggle={() => { setOverCommittedOnly(v => !v); handlePageChange(1); }}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
            <MobileSortSelect view="warehouse" viewState={whView} onChange={() => setPage(1)} />
            <InventoryColumnSelector view="warehouse" viewState={whView} />
            {isOwner && selectedItems.size > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setBulkThresholdModal(true)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Set Threshold ({selectedItems.size})
              </Button>
            )}
            </div>
          </CardHeader>
          <CardContent>
            {inventory.length === 0 && overCommittedOnly ? (
              <p className="text-center py-10 text-sm text-muted-foreground" data-testid="over-committed-empty">No over-committed items in this warehouse.</p>
            ) : inventory.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">No inventory assigned yet</p>
                <p className="text-muted-foreground mt-1 max-w-sm mx-auto">Add items from your Parts List to this warehouse to manage stock levels and alerts.</p>
                <p className="text-xs text-muted-foreground mt-3">Choose from parts already created in your Parts List.</p>
                {isOwner && (
                  <Button className="mt-4" onClick={() => openActionModal('stock')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Item
                  </Button>
                )}
              </div>
            ) : (
              <div>
                {/* Pending Warehouse Transfers */}
                <PendingTransfersSection
                  warehouseId={selectedWarehouseId}
                  onTransferAccepted={() => fetchData({ silentRetry: true })}
                />
                <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
                <table className="w-full" data-testid="warehouse-inventory-table">
                  <thead>
                    <tr className="border-b text-xs">
                      {isOwner && (
                        <th className="py-2 px-2 w-10 hidden sm:table-cell">
                          <Checkbox 
                            checked={selectedItems.size === inventory.length && inventory.length > 0}
                            onCheckedChange={toggleSelectAll}
                          />
                        </th>
                      )}
                      <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="item_name" onSort={whOnSort} className="text-left py-2 px-2 sm:px-3 text-xs">Item</SortableTableHead>
                      {col('sku') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="sku" onSort={whOnSort} className="text-left py-2 px-3 text-xs hidden sm:table-cell">SKU</SortableTableHead>}
                      {col('manufacturer_part_number') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="manufacturer_part_number" onSort={whOnSort} className="text-left py-2 px-3 text-xs hidden sm:table-cell">Manufacturer Part #</SortableTableHead>}
                      {col('location_label') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="location_label" onSort={whOnSort} className="text-left py-2 px-3 text-xs hidden sm:table-cell">Location</SortableTableHead>}
                      {col('categories') && <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500 hidden sm:table-cell">Categories</th>}
                      {col('qty_on_hand') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="qty_on_hand" onSort={whOnSort} className="!text-right py-2 px-2 text-xs hidden md:table-cell">On Hand</SortableTableHead>}
                      <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="available_qty" onSort={whOnSort} className="!text-right py-2 px-2 text-xs">Available</SortableTableHead>
                      {col('allocated_qty') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="allocated_qty" onSort={whOnSort} className="!text-right py-2 px-2 text-xs hidden md:table-cell">Allocated</SortableTableHead>}
                      {col('reserved_qty') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="reserved_qty" onSort={whOnSort} className="!text-right py-2 px-2 text-xs hidden md:table-cell">Reserved</SortableTableHead>}
                      {col('quarantined_qty') && <SortableTableHead sortKey={whSortKey} sortDir={whSortDir} columnKey="quarantined_qty" onSort={whOnSort} className="!text-right py-2 px-2 text-xs hidden md:table-cell">Quarantined</SortableTableHead>}
                      {col('effective_reorder_threshold') && <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 hidden sm:table-cell">Threshold</th>}
                      <th className="text-right py-2 px-2 sm:px-3 font-medium text-gray-500 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedInventory.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-muted/50">
                        {isOwner && (
                          <td className="py-2 px-2 hidden sm:table-cell">
                            <Checkbox 
                              checked={selectedItems.has(inv.item_id)}
                              onCheckedChange={() => toggleSelectItem(inv.item_id)}
                            />
                          </td>
                        )}
                        {/* Item — SKU merged underneath on mobile */}
                        <td className="py-2 px-2 sm:px-3">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {inv.image_thumb_url ? (
                              <img src={inv.image_thumb_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" loading="lazy" />
                            ) : null}
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <TruncatedTextWithTooltip
                                  text={inv.item_name}
                                  className="font-medium text-sm max-w-[130px] sm:max-w-[200px]"
                                />
                                {inv.ownership_type === 'consigned' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="outline" className="h-5 px-1.5 bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                                          <Handshake className="w-3 h-3" />
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Consigned from: {inv.vendor_name || 'Unknown vendor'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {inv.external_url && (
                                  <a href={inv.external_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline shrink-0">
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              {/* SKU / MPN shown under item name on mobile only */}
                              {(col('manufacturer_part_number') ? inv.manufacturer_part_number : inv.sku) && (
                                <TruncatedTextWithTooltip
                                  as="p"
                                  text={col('manufacturer_part_number') ? `MPN ${inv.manufacturer_part_number}` : inv.sku}
                                  className="text-[11px] text-muted-foreground max-w-[130px] sm:hidden"
                                />
                              )}
                            </div>
                          </div>
                        </td>
                        {col('sku') && <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{inv.sku || '-'}</td>}
                        {col('manufacturer_part_number') && (
                          <td className="py-2 px-3 hidden sm:table-cell font-mono text-xs" data-testid={`mpn-${inv.id}`}>
                            {inv.manufacturer_part_number || <span className="text-muted-foreground" title="No manufacturer part # on this catalogue item">—</span>}
                          </td>
                        )}
                        {/* Location — desktop only */}
                        {col('location_label') && <td className="py-2 px-3 hidden sm:table-cell">
                          {editingLocationLabel === inv.item_id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={newLocationLabel}
                                onChange={(e) => setNewLocationLabel(e.target.value)}
                                placeholder="e.g., Row 3 Shelf 2"
                                className="h-7 text-xs w-32"
                              />
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleUpdateLocationLabel(inv.item_id)}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingLocationLabel(null)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-1 cursor-pointer group"
                              onClick={() => {
                                if (isOwner) {
                                  setEditingLocationLabel(inv.item_id);
                                  setNewLocationLabel(inv.location_label || '');
                                }
                              }}
                            >
                              {inv.location_label ? (
                                <Badge variant="outline" className="text-xs">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {inv.location_label}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs group-hover:underline">
                                  {isOwner ? 'Add location' : '-'}
                                </span>
                              )}
                            </div>
                          )}
                        </td>}
                        {/* Categories */}
                        {col('categories') && <td className="py-2 px-2 sm:px-3 hidden sm:table-cell">
                          <div className="flex flex-wrap gap-0.5">
                            {inv.category_names?.length > 0 ? (
                              inv.category_names.map((name, idx) => (
                                <Badge key={idx} variant="secondary" className="text-[10px] sm:text-xs px-1.5 py-0">
                                  {name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            )}
                          </div>
                        </td>}
                        <QtyCells inv={inv} visible={col} onOpen={() => openBreakdown(inv.item_id, 'warehouse', selectedWarehouseId, inv.id)} />
                        {/* Threshold — desktop only */}
                        {col('effective_reorder_threshold') && <td className="py-2 px-3 text-right hidden sm:table-cell">
                          {editingThreshold === inv.item_id ? (
                            <div className="flex items-center gap-1 justify-end">
                              <div className="flex flex-col gap-1">
                                <IntegerQuantityInput
                                  min="0"
                                  value={newThreshold}
                                  onChange={(val) => setNewThreshold(val)}
                                  placeholder="0"
                                  className="h-7 text-xs w-16 text-right"
                                  disabled={useDefaultThreshold}
                                  data-testid="inline-threshold-input"
                                />
                                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Checkbox
                                    checked={useDefaultThreshold}
                                    onCheckedChange={(checked) => setUseDefaultThreshold(checked)}
                                    className="h-3 w-3"
                                  />
                                  Use default
                                </label>
                              </div>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleUpdateThreshold(inv.item_id)}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingThreshold(null); setNewThreshold(''); setUseDefaultThreshold(false); }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div
                              className={`flex items-center gap-1 justify-end ${isOwner ? 'cursor-pointer group' : ''}`}
                              onClick={() => {
                                if (isOwner) {
                                  setEditingThreshold(inv.item_id);
                                  setNewThreshold(inv.warehouse_reorder_threshold_override?.toString() || '');
                                  setUseDefaultThreshold(inv.warehouse_reorder_threshold_override === null);
                                }
                              }}
                            >
                              {inv.effective_reorder_threshold !== null ? (
                                <div className="flex items-center gap-1">
                                  <span className={`font-medium ${isOwner ? 'group-hover:underline' : ''}`}>
                                    {inv.effective_reorder_threshold}
                                  </span>
                                  <Badge 
                                    variant="outline" 
                                    className={`text-[10px] py-0 px-1 ${
                                      inv.warehouse_reorder_threshold_override !== null 
                                        ? 'bg-primary/10 text-primary border-primary/30' 
                                        : 'bg-muted text-muted-foreground'
                                    }`}
                                  >
                                    {inv.warehouse_reorder_threshold_override !== null ? 'WH' : 'Default'}
                                  </Badge>
                                </div>
                              ) : (
                                <span className={`text-muted-foreground text-xs ${isOwner ? 'group-hover:underline' : ''}`}>
                                  {isOwner ? 'Set threshold' : '-'}
                                </span>
                              )}
                            </div>
                          )}
                        </td>}
                        {/* Actions — ⋮ dropdown */}
                        <td className="py-2 px-2 sm:px-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`warehouse-actions-${inv.item_id}`} onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="h-4 w-4 text-gray-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {(isOwner || hasPerm('warehouse.adjust')) && (
                                <DropdownMenuItem
                                  onClick={() => setTimeout(() => setAdjustmentModal({ open: true, itemId: inv.item_id, itemName: inv.item_name, currentQty: inv.qty_on_hand, defaultType: null }), 0)}
                                  data-testid={`adjust-warehouse-item-${inv.item_id}`}
                                >
                                  <Settings2 className="h-3.5 w-3.5 mr-2" /> Adjust
                                </DropdownMenuItem>
                              )}
                              {(isOwner || hasPerm('jobs.parts.assign')) && (
                                <DropdownMenuItem
                                  onClick={() => setTimeout(() => setAssignJobModal({ open: true, item: inv }), 0)}
                                  data-testid={`assign-job-${inv.item_id}`}
                                >
                                  <Briefcase className="h-3.5 w-3.5 mr-2" /> Assign to Job
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => setTimeout(() => setTransferModal({ open: true, item: { id: inv.item_id, name: inv.item_name } }), 0)}
                                data-testid={`transfer-warehouse-item-${inv.item_id}`}
                              >
                                <ArrowRightLeft className="h-3.5 w-3.5 mr-2" /> Transfer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Phase 3.5: Pagination Controls */}
                <ListPagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  loading={loading}
                  pageSizeOptions={[15, 25, 50, 100]}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit History Section */}
        {canManageCycleCounts && selectedWarehouseId && (
          <CycleCountHistory 
            targetType="warehouse" 
            targetId={selectedWarehouseId}
            returnTo="/warehouse"
          />
        )}
      </div>

      {/* Action Modal */}
      <Dialog open={actionModal.open} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {actionModal.type === 'stock' && 'Stock Warehouse'}
            </DialogTitle>
            <DialogDescription>
              {actionModal.type === 'stock'
                ? 'Choose from parts already created in your Parts List.'
                : 'Update warehouse inventory'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {actionModal.type === 'stock' ? (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {bulkItems.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3 space-y-3 bg-muted/30">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <AsyncSearchableSelect
                          onSearch={handleItemSearch}
                          value={item.item_id}
                          onValueChange={(val) => { updateBulkItem(index, 'item_id', val); updateBulkItem(index, 'name', ''); }}
                          placeholder="Search by name or SKU…"
                          emptyText="No items found. Try a different search."
                          className="h-9"
                          data-testid={`stock-item-select-${index}`}
                          allowCreate
                          onCreateSelected={(name) => { updateBulkItem(index, 'name', name); updateBulkItem(index, 'item_id', ''); }}
                        />
                        <div className="flex flex-col sm:flex-row gap-2">
                          <IntegerQuantityInput
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(val) => updateBulkItem(index, 'quantity', val)}
                            className="w-full sm:w-20 h-9"
                            min="1"
                            data-testid={`stock-qty-${index}`}
                          />
                          <Input
                            placeholder="Location (optional)"
                            value={item.location_label}
                            onChange={(e) => updateBulkItem(index, 'location_label', e.target.value)}
                            className="flex-1 h-9"
                            data-testid={`stock-location-${index}`}
                          />
                        </div>
                        
                        {/* Consigned Inventory - Ownership Selector */}
                        {consignedInventoryEnabled && (
                          <div className="space-y-2 pt-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-muted-foreground whitespace-nowrap">Ownership:</Label>
                              <div className="flex rounded-md border overflow-hidden">
                                <button
                                  type="button"
                                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                                    item.ownership_type === 'owned' 
                                      ? 'bg-primary text-primary-foreground' 
                                      : 'bg-background hover:bg-muted'
                                  }`}
                                  onClick={() => updateBulkItem(index, 'ownership_type', 'owned')}
                                  data-testid={`ownership-owned-${index}`}
                                >
                                  Owned
                                </button>
                                <button
                                  type="button"
                                  className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
                                    item.ownership_type === 'consigned' 
                                      ? 'bg-amber-500 text-white' 
                                      : 'bg-background hover:bg-muted'
                                  }`}
                                  onClick={() => updateBulkItem(index, 'ownership_type', 'consigned')}
                                  data-testid={`ownership-consigned-${index}`}
                                >
                                  <Handshake className="w-3 h-3 inline mr-1" />
                                  Consigned
                                </button>
                              </div>
                            </div>
                            
                            {/* Vendor Dropdown - Only shown for consigned */}
                            {item.ownership_type === 'consigned' && (
                              <SearchableSelect
                                options={vendors.map(v => ({ 
                                  value: v.id, 
                                  label: v.name 
                                }))}
                                value={item.vendor_id}
                                onValueChange={(val) => updateBulkItem(index, 'vendor_id', val)}
                                placeholder="Select vendor (required)"
                                searchPlaceholder="Search vendors..."
                                emptyText="No vendors found."
                                className="h-9"
                                data-testid={`vendor-select-${index}`}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 mt-0 shrink-0"
                        onClick={() => removeBulkItem(index)}
                        disabled={bulkItems.length === 1}
                        data-testid={`remove-item-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addBulkItem} className="w-full" data-testid="add-another-item">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Item
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Item</Label>
                  <SearchableSelect
                    options={inventory.map(inv => ({ 
                      value: inv.id, // Use inventory ID to identify specific record
                      label: `${inv.item_name} (${inv.qty_on_hand} available)${inv.ownership_type === 'consigned' ? ' 🤝' : ''}` 
                    }))}
                    value={selectedItemId}
                    onValueChange={setSelectedItemId}
                    placeholder="Select item"
                    searchPlaceholder="Search items..."
                    emptyText="No items found."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <IntegerQuantityInput
                    value={quantity}
                    onChange={(val) => setQuantity(val)}
                    min="1"
                    data-testid="add-stock-qty-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                  />
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={closeModal} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={actionModal.type === 'stock' ? handleBulkStock : handleAction}
                disabled={actionLoading}
                className="flex-1"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {actionModal.type === 'stock' && 'Stock All'}
                {actionModal.type === 'restock' && 'Restock'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Threshold Modal */}
      {/* Set Threshold Drawer */}
      <AppDrawer
        open={bulkThresholdModal}
        onOpenChange={(open) => !open && setBulkThresholdModal(false)}
        title="Set Warehouse Threshold"
        description={`Update the reorder threshold for ${selectedItems.size} selected item(s). This overrides the catalogue default for warehouse stock only.`}
        size="md"
        isDirty={!!bulkThresholdValue}
        data-testid="set-threshold-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setBulkThresholdModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkThresholdUpdate} data-testid="apply-threshold-btn">
              Update {selectedItems.size} Items
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Threshold Value</Label>
            <IntegerQuantityInput
              min="0"
              value={bulkThresholdValue}
              onChange={(val) => setBulkThresholdValue(val)}
              placeholder="Enter threshold"
              disabled={bulkUseDefault}
              data-testid="bulk-threshold-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="bulk-use-default"
              checked={bulkUseDefault}
              onCheckedChange={(checked) => setBulkUseDefault(checked)}
              data-testid="bulk-use-default-checkbox"
            />
            <Label htmlFor="bulk-use-default" className="text-sm cursor-pointer">
              Use catalogue default (clear warehouse override)
            </Label>
          </div>
        </div>
      </AppDrawer>

      {/* Threshold Import Drawer */}
      <AppDrawer
        open={thresholdImportModal}
        onOpenChange={(open) => !open && closeThresholdImport()}
        title="Import Warehouse Thresholds (CSV)"
        description="Bulk import reorder threshold overrides for warehouse items via CSV file."
        size="md"
        isDirty={thresholdImportStep === 'preview'}
        data-testid="import-threshold-drawer"
        footer={
          thresholdImportStep === 'preview' ? (
            <>
              <Button variant="outline" onClick={() => { setThresholdImportStep('upload'); setThresholdImportFile(null); setThresholdImportPreview(null); }}>
                Back
              </Button>
              <Button 
                onClick={handleApplyThresholdImport} 
                disabled={thresholdImportLoading || (thresholdImportPreview?.summary.will_update_count === 0 && thresholdImportPreview?.summary.will_create_count === 0)}
                data-testid="apply-import-btn"
              >
                {thresholdImportLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Apply Import
              </Button>
            </>
          ) : thresholdImportStep === 'results' ? (
            <Button onClick={closeThresholdImport} data-testid="import-done-btn">Done</Button>
          ) : null
        }
      >
          {/* Upload Step */}
          {thresholdImportStep === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a CSV file with <code className="bg-muted px-1 rounded">sku</code> and <code className="bg-muted px-1 rounded">threshold</code> columns
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleThresholdFileSelect}
                  className="hidden"
                  id="threshold-csv-input"
                />
                <div className="flex justify-center gap-2">
                  <Button asChild disabled={thresholdImportLoading}>
                    <label htmlFor="threshold-csv-input" className="cursor-pointer">
                      {thresholdImportLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Select CSV File
                    </label>
                  </Button>
                  <Button variant="outline" onClick={downloadThresholdTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                <p className="font-medium mb-1">CSV Format:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><code className="bg-muted px-1 rounded">sku</code> - Required. Must match an existing catalogue item SKU.</li>
                  <li><code className="bg-muted px-1 rounded">threshold</code> - Required. Integer ≥ 0. Blank values will error.</li>
                </ul>
                <p className="mt-2 text-muted-foreground/80">
                  Imports to your warehouse. Existing threshold overrides will be updated.
                </p>
              </div>
            </div>
          )}

          {/* Preview Step */}
          {thresholdImportStep === 'preview' && thresholdImportPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-3 bg-green-50 border-green-200">
                  <p className="text-xs text-green-600 font-medium">Will Update</p>
                  <p className="text-2xl font-bold text-green-700">{thresholdImportPreview.summary.will_update_count}</p>
                </Card>
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">Will Create</p>
                  <p className="text-2xl font-bold text-blue-700">{thresholdImportPreview.summary.will_create_count}</p>
                </Card>
                <Card className="p-3 bg-red-50 border-red-200">
                  <p className="text-xs text-red-600 font-medium">Errors</p>
                  <p className="text-2xl font-bold text-red-700">{thresholdImportPreview.summary.error_count}</p>
                </Card>
              </div>

              {/* Will Update Table */}
              {thresholdImportPreview.will_update.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2">Will Update ({thresholdImportPreview.will_update.length})</p>
                  <div className="border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Current</th>
                          <th className="text-right p-2">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportPreview.will_update.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono text-xs">{row.sku}</td>
                            <td className="p-2 max-w-32">
                              <TruncatedTextWithTooltip text={row.item_name} />
                            </td>
                            <td className="p-2 text-right">{row.current_threshold ?? '-'}</td>
                            <td className="p-2 text-right font-medium text-green-700">{row.new_threshold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Will Create Table */}
              {thresholdImportPreview.will_create.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-blue-700 mb-2">Will Create ({thresholdImportPreview.will_create.length})</p>
                  <div className="border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Threshold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportPreview.will_create.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono text-xs">{row.sku}</td>
                            <td className="p-2 max-w-32">
                              <TruncatedTextWithTooltip text={row.item_name} />
                            </td>
                            <td className="p-2 text-right font-medium text-blue-700">{row.new_threshold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors Table */}
              {thresholdImportPreview.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">Errors ({thresholdImportPreview.errors.length})</p>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Row</th>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportPreview.errors.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{row.row}</td>
                            <td className="p-2 font-mono text-xs">{row.sku || '-'}</td>
                            <td className="p-2 text-red-600">{row.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Results Step */}
          {thresholdImportStep === 'results' && thresholdImportResults && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-lg font-semibold text-green-700">Import Complete!</p>
                <p className="text-sm text-green-600">
                  Updated {thresholdImportResults.summary.updated_count} items, 
                  created {thresholdImportResults.summary.created_count} records
                  {thresholdImportResults.summary.error_count > 0 && `, ${thresholdImportResults.summary.error_count} errors`}
                </p>
              </div>

              {/* Updated Items */}
              {thresholdImportResults.updated.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Updated ({thresholdImportResults.updated.length})</p>
                  <div className="border rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Old</th>
                          <th className="text-right p-2">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportResults.updated.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono text-xs">{row.sku}</td>
                            <td className="p-2 max-w-32">
                              <TruncatedTextWithTooltip text={row.item_name} />
                            </td>
                            <td className="p-2 text-right">{row.old_threshold ?? '-'}</td>
                            <td className="p-2 text-right font-medium">{row.new_threshold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Created Items */}
              {thresholdImportResults.created.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Created ({thresholdImportResults.created.length})</p>
                  <div className="border rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Threshold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportResults.created.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono text-xs">{row.sku}</td>
                            <td className="p-2 max-w-32">
                              <TruncatedTextWithTooltip text={row.item_name} />
                            </td>
                            <td className="p-2 text-right font-medium">{row.new_threshold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {thresholdImportResults.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">Errors ({thresholdImportResults.errors.length})</p>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Row</th>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thresholdImportResults.errors.map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{row.row}</td>
                            <td className="p-2 font-mono text-xs">{row.sku || '-'}</td>
                            <td className="p-2 text-red-600">{row.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
      </AppDrawer>

      {/* Scanner */}
      <ScanManager 
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        context="warehouse"
        onActionComplete={fetchData}
      />

      {/* Add Warehouse Modal */}
      {/* Add Warehouse Drawer */}
      <AppDrawer
        open={addWarehouseModal}
        onOpenChange={(open) => {
          if (!open) {
            setAddWarehouseModal(false);
            setNewWarehouseName('');
          }
        }}
        title="Add New Warehouse"
        description="Create an additional warehouse location for your inventory."
        size="md"
        isDirty={!!newWarehouseName.trim()}
        data-testid="add-warehouse-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => { setAddWarehouseModal(false); setNewWarehouseName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateWarehouse} disabled={!newWarehouseName.trim() || addWarehouseLoading} data-testid="save-warehouse-btn">
              {addWarehouseLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Warehouse
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Warehouse Name</label>
            <Input
              value={newWarehouseName}
              onChange={(e) => setNewWarehouseName(e.target.value)}
              placeholder="e.g., East Branch, Downtown Storage"
              className="mt-1"
              data-testid="warehouse-name-input"
            />
          </div>
          {planLimits?.warehouse && (
            <p className="text-xs text-muted-foreground">
              {planLimits.warehouse.limit !== null 
                ? `${Math.min(planLimits.warehouse.current_count, planLimits.warehouse.limit)} of ${planLimits.warehouse.limit} warehouses used`
                : `${planLimits.warehouse.current_count} warehouses (unlimited)`
              }
            </p>
          )}
        </div>
      </AppDrawer>

      {/* Manual Adjustment Modal */}
      {(isOwner || hasPerm('warehouse.adjust')) && selectedWarehouse && (
        <ManualAdjustmentModal
          open={adjustmentModal.open}
          onClose={(success) => {
            setAdjustmentModal({ open: false, itemId: null, itemName: '', currentQty: 0, defaultType: null });
            if (success) fetchData({ silentRetry: true });
          }}
          locationType="warehouse"
          locationId={selectedWarehouseId}
          locationName={selectedWarehouse?.name || ''}
          itemId={adjustmentModal.itemId}
          itemName={adjustmentModal.itemName}
          currentQuantity={adjustmentModal.currentQty}
          defaultAdjustmentType={adjustmentModal.defaultType}
        />
      )}

      {/* Assign to Job Modal */}
      <AssignToJobModal
        open={assignJobModal.open}
        onOpenChange={(open) => setAssignJobModal({ open, item: open ? assignJobModal.item : null })}
        item={assignJobModal.item}
        warehouseId={selectedWarehouseId}
        onSuccess={() => fetchData({ silentRetry: true })}
      />

      {/* Universal Transfer Modal */}
      <TransferModal
        open={transferModal.open}
        onOpenChange={(open) => setTransferModal({ open, item: open ? transferModal.item : null })}
        onSuccess={() => fetchData({ silentRetry: true })}
        preselectedItem={transferModal.item}
        preselectedFromLocation={selectedWarehouseId}
        preselectedFromType="warehouse"
        preselectedFromName={selectedWarehouse?.name}
        sourceReadOnly={true}
      />

      {/* Start Cycle Count Modal */}
      {canManageCycleCounts && selectedWarehouse && (
        <StartCycleCountModal
          open={cycleCountModalOpen}
          onOpenChange={setCycleCountModalOpen}
          targetType="warehouse"
          targetId={selectedWarehouseId}
          targetName={selectedWarehouse?.name || 'Warehouse'}
          returnTo="/warehouse"
        />
      )}

      {/* Warehouse Details Modal */}
      {canManageWarehouses && selectedWarehouse && (
        <WarehouseDetailsModal
          open={warehouseDetailsOpen}
          onOpenChange={setWarehouseDetailsOpen}
          warehouse={selectedWarehouse}
          onSaved={() => {
            // Refresh warehouse data after save
            refreshWarehouses();
          }}
        />
      )}

      {/* Mobile FAB */}
      <MobileFABContainer data-testid="warehouse-fab-container">
        <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
          {canManageWarehouses && (
            <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); openActionModal('stock'); }}>
              Stock
            </MobileFABMenuItem>
          )}
          <MobileFABMenuItem icon={ScanLine} onClick={() => { setFabOpen(false); setScannerOpen(true); }}>
            Scan
          </MobileFABMenuItem>
          {canManageWarehouses && planLimits?.warehouse?.can_create && (
            <MobileFABMenuItem icon={WarehouseIcon} onClick={() => { setFabOpen(false); handleAddWarehouseClick(); }}>
              Add Warehouse
            </MobileFABMenuItem>
          )}
        </MobileFABMenu>
        <button
          className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
          onClick={() => setFabOpen(!fabOpen)}
          data-testid="warehouse-mobile-fab"
        >
          <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
        </button>
      </MobileFABContainer>
      <AvailabilityBreakdownModal open={breakdownState.open} onOpenChange={setBreakdownOpen} data={breakdownState.data} loading={breakdownState.loading} />
    </Layout>
  );
};
